import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Brand } from '../../../../core/models/brand';
import { DashboardReportingQueryTypes, IReportDateRange, ReportDao } from '../../../../core/services/dao/reportDao';
import * as moment from 'moment-timezone';
import { ConstantMan } from '../../../../core/services/constantMan';
import { DemoConstants } from '../../../../assets/data/demoConstants';


interface ITimeOfDayQueryResponse {
  affiliate: string;
  baselineSessionCount: number;
  baselineSessionsPerSecond: number;
  creativeLength: string;
  eventTimestamp: string;
  dmaName: string;
  eventId: string;
  eventLength: number;
  isci: string;
  lift: number;
  pvCount: number;
  sessionCount: number;
  timeOfDay: string;
  timezone: string;
}

interface ICreativeMapValue {
  sessionCount: number;
  pvCount: number;
  airingCount: number;
  baselineSessionCount: number;
  // avgLift: number;
}

@Component({
  selector: 'time-of-day-tile',
  templateUrl: './time-of-day-tile.component.html',
  styleUrls: ['time-of-day-tile.component.scss']
})
export class TimeOfDayTileComponent implements OnInit, OnDestroy {
  dateRangeSubscription: Subscription;
  @Input() brand: Brand;
  @Input() timezone: string;
  public currentDateRange: IReportDateRange;
  private subs: any[] = [];
  public loadingData = true;

  public liftChartData: any  = { series: [], labels: [] };
  // public liftChartOpts: BarChartOptions = {};
  public liftChartEvents = {};
  public lengthChartData: any = { series: [], labels: [] };
  // public lengthChartOpts: PieChartOptions = {};
  public lengthDisplayField = 'sessions';
  public allRecords: any;
  public selectedPage = 1;
  public creatives = [];
  public selectedTimeOfDay = 'all';
  private creativeMapping: any;
  private affiliateMapping: any;
  private lengthMapping: any;
  private affiliates = new Set();
  readonly chartColors: string[];
  private maxSpots = 6;
  private listeners = [];
  public timeOfDayOptions = [
    {
      label: 'All',
      value: 'all',
      validHours: []
    },
    {
      label: 'Late Fringe (12am-1am)',
      value: 'late_fringe',
      validHours: [0]
    },
    {
      label: 'Post Late Fringe (1am-2am)',
      value: 'post_late_fringe',
      validHours: [1]
    },
    {
      label: 'Overnight (2am-6am)',
      value: 'overnight',
      validHours: [2, 3, 4, 5]
    },
    {
      label: 'Early Morning (6am-10am)',
      value: 'early_morning',
      validHours: [6, 7, 8, 9]
    },
    {
      label: 'Daytime (10am-5pm)',
      value: 'daytime',
      validHours: [10, 11, 12, 13, 14, 15, 16]
    },
    {
      label: 'Early Fringe (5pm-7pm)',
      value: 'early_fringe',
      validHours: [17, 18]
    },
    {
      label: 'Prime Access (7pm-8pm)',
      value: 'prime_access',
      validHours: [19]
    },
    {
      label: 'Prime Time (8pm-11pm)',
      value: 'prime_time',
      validHours: [20, 21, 22]
    },
    {
      label: 'Late News (11pm-12am)',
      value: 'late_news',
      validHours: [23]
    }
  ];

  constructor() {
    this.chartColors = ConstantMan.CHARTIST_COLORS;
  }

  ngOnInit() {
    this.fetchTileData();
  }

  fetchTileData() {
    this.allRecords = DemoConstants.timeOfDay;
    this.initAffiliateCharts(DemoConstants.timeOfDay);
    this.loadingData = false;
  }

  initAffiliateCharts(records) {
    let affiliateMapping = {};
    let creativeMapping = {};
    let lengthMapping = {};
    for (let record of records) {
      this.updateAffiliateMapping(record, affiliateMapping);
      this.updateCreativeMapping(record, creativeMapping);
      this.updateLengthMapping(record, lengthMapping);
    }
    this.lengthMapping = lengthMapping;
    this.creativeMapping = creativeMapping;
    this.affiliateMapping = affiliateMapping;
    this.setLiftChartData();
    this.setLengthChartData();
  }

  updateLengthMapping(record, mapping) {
    if (!mapping[record.eventLength]) {
      mapping[record.eventLength] = [record];
    } else {
      let tmp = mapping[record.eventLength];
      tmp.push(record);
      mapping[record.eventLength] = tmp;
    }
  }

  ///need creative mapping with obj for each affilate
  private updateCreativeMapping(record, map): void {
    this.affiliates.add(record.affiliate);
    let value = {
      sessionCount: record.sessionCount,
      pvCount: record.pvCount,
      airingCount: 1,
      baselineSessionCount: record.baselineSessionCount,
      lift_values: [this.getPercentLift(record)]
    } as ICreativeMapValue;
    if (!map[record.isci]) {
      map[record.isci] = {};
      map[record.isci][record.affiliate] = value;
    } else {
      let tmp = map[record.isci];
      if (!tmp[record.affiliate]) {
        tmp[record.affiliate] = value;
      } else {
        tmp[record.affiliate].sessionCount = tmp[record.affiliate].sessionCount += record.sessionCount;
        tmp[record.affiliate].pvCount = tmp[record.affiliate].pvCount += record.pvCount;
        tmp[record.affiliate].airingCount = tmp[record.affiliate].airingCount += record.airingCount;
        tmp[record.affiliate].baselineSessionCount = tmp[record.affiliate].baselineSessionCount +=
          record.baselineSessionCount;
        tmp[record.affiliate].lift_values.push(this.getPercentLift(record));
      }
      map[record.isci] = tmp;
    }
  }

  private updateAffiliateMapping(record, map): void {
    let value = {
      sessionCount: record.sessionCount,
      pvCount: record.pvCount,
      airingCount: 1,
      baselineSessionCount: record.baselineSessionCount
    } as ICreativeMapValue;
    if (!map[record.affiliate]) {
      map[record.affiliate] = {};
      map[record.affiliate][record.isci] = value;
    } else {
      let tmp = map[record.affiliate];
      if (!tmp[record.isci]) {
        tmp[record.isci] = value;
      } else {
        tmp[record.isci].sessionCount = tmp[record.isci].sessionCount += record.sessionCount;
        tmp[record.isci].pvCount = tmp[record.isci].pvCount += record.pvCount;
        tmp[record.isci].airingCount = tmp[record.isci].airingCount += record.airingCount;
        tmp[record.isci].baselineSessionCount = tmp[record.isci].baselineSessionCount += record.baselineSessionCount;
      }
      map[record.affiliate] = tmp;
    }
  }

  changeLengthDisplayValue(evt) {
    this.lengthDisplayField = evt.value;
    this.setLengthChartData();
  }

  changeSelectedTimeOfDay(evt) {
    this.selectedTimeOfDay = evt.value;
    this.refreshChart();
  }

  refreshChart() {
    let records =
      this.selectedTimeOfDay === 'all'
        ? this.allRecords
        : this.allRecords.filter(i => i.timeOfDay === this.selectedTimeOfDay);
    this.initAffiliateCharts(records);
  }

  setLengthChartData() {
    let mapping = {};
    let totalVal = 0;
    for (let length of Object.keys(this.lengthMapping)) {
      if (this.lengthMapping[length].length > 1) {
        this.lengthMapping[length].forEach(record => {
          if (this.lengthDisplayField === 'sessions') {
            totalVal += record.sessionCount;
          } else {
            totalVal += (record.sessionCount - record.baselineSessionCount);
          }
          let tmp = mapping[record.eventLength]
            ? mapping[record.eventLength]
            : { sessionCount: 0, baselineSessionCount: 0, airingCount: 0, lift: 0 };
          tmp['sessionCount'] += record.sessionCount;
          tmp['baselineSessionCount'] += record.baselineSessionCount;
          tmp['airingCount'] += 1;
          tmp['lift'] += (record.sessionCount - record.baselineSessionCount)
          mapping[record.eventLength] = tmp;
        });
      } else {
        mapping[length] = this.lengthMapping[length][0];
      }
    }
    let labels = Object.keys(mapping).map(l => (l && l !== 'null' ? l + 's' : 'Unknown'));
    let series = [];
    Object.values(mapping).forEach(val => {
      let seriesObj = {
        value: 0,
        meta: `<div style="text-align: start">
                ${(val as any).lift.toFixed(0)} total lift during micro-moment
                </div>
                <div style="text-align: start">
                    5 minutes before: ${val['baselineSessionCount']} sessions
                </div>
                <div style="text-align: start">
                    5 minutes after: ${val['sessionCount']} sessions
                </div>
        `,
        name: `${length}_seconds`
      };
      if (this.lengthDisplayField === 'sessions') {
        seriesObj.value = (val as any).sessionCount;
      } else {
        seriesObj.value = (val as any).lift;
      }
      series.push(seriesObj);
    });
    this.lengthChartData.labels = labels;
    this.lengthChartData.series = series;
    let legendContainer = document.getElementById('tod-length-legend');
    let currentLegend = document.getElementsByClassName('tod-length-legend-el').item(0);
    if (currentLegend) currentLegend.remove();
    // this.lengthChartOpts = {
    //   showLabel: true,
    //   labelPosition: 'inside',
    //   startAngle: 0,
    //   labelInterpolationFnc: (value, idx) => {
    //     return (
    //       Math.round(
    //         (series[idx].value /
    //           series.reduce((acc, s) => {
    //             return acc + s.value;
    //           }, 0)) *
    //           100
    //       ) + '%'
    //     );
    //   },
    //   plugins: [
    //
    //   ]
    // } as PieChartOptions;
  }

  ///why does this break at 13?
  getTopNCreativesFromCreativeMapping(creativeMapping: any) {
    let countMapping = {};
    for (let spotKey of Object.keys(creativeMapping)) {
      let total = 0;
      for (let affiliateKey of Object.keys(creativeMapping[spotKey])) {
        total += creativeMapping[spotKey][affiliateKey].sessionCount;
      }
      countMapping[spotKey] = total;
    }
    let sortedCounts = (Object.values(countMapping) as number[])
      .sort((a, b) => {
        return b - a;
      })
      .slice(0, this.maxSpots);
    let topSpots = {};
    for (const [key, value] of Object.entries(countMapping)) {
      if (sortedCounts.includes(value as number) && Object.keys(topSpots).length < this.maxSpots) {
        topSpots[key] = creativeMapping[key];
      }
    }
    return topSpots;
  }

  getTopAffiliates(): Array<string> {
    let affiliates = [];
    for (let affiliateKey of Object.keys(this.affiliateMapping)) {
      let sum = Object.values(this.affiliateMapping[affiliateKey]).reduce((val, s) => {
        return s + val['airingCount'];
      }, 0);
      affiliates.push({
        affiliate: affiliateKey,
        value: sum
      });
    }
    return affiliates
      .sort((a, b) => {
        return b.value - a.value;
      })
      .slice(0, 12)
      .map(item => item.affiliate);
  }

  getPercentLift(record): number {
    return parseInt((record.lift * 100).toFixed(0));
  }

  formatPercentLift(lift: number): string {
    return lift.toFixed(0) + '%';
  }

  setLiftChartData() {
    this.creativeMapping = this.getTopNCreativesFromCreativeMapping(this.creativeMapping);
    let labels = this.getTopAffiliates();
    let series = [];
    let maxY = 0;
    let secondMaxY = 0;
    let minY = 0;
    let spots = Object.keys(this.creativeMapping).sort();
    for (let spot of spots) {
      series.push({
        label: spot,
        name: spot,
        data: [
          ...Array(labels.length).fill({
            meta: '',
            value: Number.NEGATIVE_INFINITY
          })
        ]
      });
    }
    //creative mapping = Map(creative,Map(affiliate, data))
    let affiliateIndex = 0;
    for (let affiliate of labels) {
      let hasAny = false;
      let spotIndex = 0;
      for (let spot of spots) {
        if (this.creativeMapping[spot][affiliate]) {
          hasAny = true;
          let val = this.creativeMapping[spot][affiliate];
          const averageLift = val.lift_values.reduce((a, b) => a + b) / val.lift_values.length;
          if (averageLift > maxY) {
            maxY = averageLift;
          }
          if (averageLift < minY) {
            minY = averageLift;
          }
          if (averageLift > secondMaxY && (averageLift < maxY || !maxY)) {
            secondMaxY = averageLift;
          }

          let updated = {
            meta: ` <div style="text-align: start;font-weight: bold; color: ${this.chartColors[spotIndex]}">
                      ${spot}
                  </div>
                  <div style="text-align: start;">${affiliate}</div>
                  <div style="text-align: start">
                      ${this.formatPercentLift(averageLift)} average lift in Micro-moment
                  </div>
                  <div style="text-align: start">
                      5 minutes before: ${val.baselineSessionCount} sessions
                  </div>
                  <div style="text-align: start">
                      5 minutes during: ${val.sessionCount} sessions
                  </div>`,
            value: averageLift
          };
          series[spotIndex].data.splice(affiliateIndex, 1, updated);
        }
        spotIndex++;
      }

      if (!hasAny) {
        series.forEach(s => s.data.splice(affiliateIndex, 1));
        labels.splice(affiliateIndex, 1);
      }

      affiliateIndex++;
    }

    this.liftChartData.series = series;
    this.liftChartData.labels = labels;

    let legendContainer = document.getElementById('tod-activity-legend');
    // this.liftChartOpts = {
    //   seriesBarDistance: 12,
    //   axisX: {
    //     showGrid: false
    //   },
    //   plugins: [
    //
    //   ]
    // } as BarChartOptions;
    // this.liftChartEvents = {
    //   draw: data => {
    //     ///add label to bar
    //     if (data.type === 'bar') {
    //       data.element._node.style.strokeWidth = '10px';
    //       if (data.value.y > 0) {
    //         data.group
    //           .elem(
    //             'text',
    //             {
    //               // align on top
    //               x: data.x1,
    //               y: data.y2 - 5,
    //               style: 'text-anchor: middle; cursor: default; font-stretch: ultra-condensed; font-size: .75em'
    //             },
    //             'bar-label'
    //           )
    //           .text(this.formatPercentLift(data.value.y));
    //       }
    //       if (data.value.y < 0 && data.value.y > Number.NEGATIVE_INFINITY) {
    //         //align on bottom
    //         data.group
    //           .elem(
    //             'text',
    //             {
    //               // This gets the top point of the bars and then adds enough room for label
    //               x: data.x1,
    //               y: data.y2 + 20,
    //               dy: -10,
    //               style: 'text-anchor: middle; cursor: default; font-stretch: ultra-condensed; font-size: .75em'
    //             },
    //             'bar-label'
    //           )
    //           .text(this.formatPercentLift(data.value.y));
    //       }
    //     }
    //   }
    // };
  }

  chartInitialized(chart) {
    this.listeners.push(
      window.matchMedia('print').addEventListener('change', e => {
        chart.update();
      })
    );
  }

  ngOnDestroy() {
    if (this.listeners && this.listeners.length > 0) {
      for (let listener of this.listeners) {
        window.matchMedia('print').removeEventListener('beforeprint', listener);
      }
    }
    this.subs.forEach(sub => sub.unsubscribe());
  }
}
